import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import Navbar from './Components/Navbar';
import Footer from './Components/Footer';
import WhatWeOffer from './Components/WhatWeOffer';
import WhyChooseTheAdox from './Components/WhyChooseTheAdox';
import ThePeopleBehind from './Components/ThePeopleBehind';
import LetsConnect from './Components/LetsConnect';
import CursorParticle from './Components/CursorParticle';
import EnhancedBackground from './Components/EnhancedBackground';
import LoadingScreen from './Components/LoadingScreen';
import { FaCog } from 'react-icons/fa';
import logoImage from '../images/Logo_addox.png';
import companyPicture1 from '../images/Company Picture 1 1.png';
import companyPicture2 from '../images/Company Picture 2 1.png';

// Slideshow images - Replaceable array of images
// Layout: Logo → Company Photo 1 → Logo → Company Photo 2
const SLIDESHOW_IMAGES = [
    null, // Slide 1 - Logo (always shows logo)
    companyPicture1, // Slide 2 - Company Picture 1
    null, // Slide 3 - Logo (always shows logo)
    companyPicture2, // Slide 4 - Company Picture 2
];

const UserLandingPage = () => {
    const landingSectionRef = useRef(null);
    const pageRef = useRef(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slideSpeed] = useState(5000); // 5 seconds delay
    const slideshowIntervalRef = useRef(null);
    const slideshowTimeoutRef = useRef(null);
    const [buttonScale, setButtonScale] = useState(1);
    const buttonRef = useRef(null);
    const [headingScale, setHeadingScale] = useState(1);
    const headingRef = useRef(null);
    const descriptionRef = useRef(null);
    const [headingWidth, setHeadingWidth] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [backgroundEnabled, setBackgroundEnabled] = useState(true);
    const [cursorEnabled, setCursorEnabled] = useState(true);
    const [contentVisible, setContentVisible] = useState(false);
    // Phone only (< 768px): no particles, static background, no scroll snap. Tablet/iPad get particles + animation + scroll snap.
    const [isPhoneOnly, setIsPhoneOnly] = useState(() =>
        typeof window !== 'undefined' && window.innerWidth < 768
    );
    // Tablet/iPad (768px–1367px): no scroll snap on Lets Connect section
    const [isTablet, setIsTablet] = useState(() =>
        typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1368
    );
    // Show Scroll Down on desktop only; hide Swipe Down on phones, tablets, iPads
    const showScrollDownIndicator = !isTablet && !isPhoneOnly;

    // Memoize slideshow images to prevent unnecessary re-renders
    const slideshowImages = useMemo(() => SLIDESHOW_IMAGES, []);

    // Helper function to schedule next slide with appropriate delay
    const scheduleNextSlide = useCallback((slideIndex) => {
        // Don't schedule if loading screen transition is not complete
        if (!contentVisible) {
            return;
        }
        
        // Clear any existing timeout
        if (slideshowTimeoutRef.current) {
            clearTimeout(slideshowTimeoutRef.current);
        }
        
        // Determine delay based on current slide
        // First slide (logo) = 7 seconds (longest), others = 5 seconds
        const delay = slideIndex === 0 ? 7000 : slideSpeed;
        
        slideshowTimeoutRef.current = setTimeout(() => {
            // Check again before advancing (in case contentVisible changed)
            if (!contentVisible) {
                return;
            }
            
            setCurrentSlide((prev) => {
                const nextSlide = (prev + 1) % slideshowImages.length;
                // Recursively schedule next slide
                scheduleNextSlide(nextSlide);
                return nextSlide;
            });
        }, delay);
    }, [slideSpeed, slideshowImages.length, contentVisible]);

    // Phone-only and tablet detection for resize (particles, scroll snap)
    useEffect(() => {
        const checkPhoneOnly = () => {
            setIsPhoneOnly(typeof window !== 'undefined' && window.innerWidth < 768);
        };
        const checkTablet = () => {
            const w = typeof window !== 'undefined' ? window.innerWidth : 0;
            setIsTablet(w >= 768 && w < 1368);
        };
        window.addEventListener('resize', checkPhoneOnly);
        window.addEventListener('resize', checkTablet);
        return () => {
            window.removeEventListener('resize', checkPhoneOnly);
            window.removeEventListener('resize', checkTablet);
        };
    }, []);

    // Scroll snapping behavior - skip on phone (< 768) and tablet/iPad (768–1367) so swipe/scroll works to Lets Connect
    useEffect(() => {
        const handleWheel = (e) => {
            if (typeof window === 'undefined') return;
            const w = window.innerWidth;
            if (w < 768) return; // phone: no lock
            if (w >= 768 && w < 1368) return; // tablet/iPad: no lock so swipe down reaches Lets Connect & Footer
            if (Math.abs(e.deltaY) > 50) {
                const sections = document.querySelectorAll('.snap-section');
                const currentScroll = window.scrollY;
                
                // Find "Why Choose" section
                const whyChooseSection = document.querySelector('#why-choose');
                // Find "The People Behind" section
                const peopleBehindSection = document.querySelector('.people-behind-section');
                
                let isInWhyChoose = false;
                let isInPeopleBehind = false;
                
                // Check if we're in "Why Choose" section
                if (whyChooseSection) {
                    const sectionTop = whyChooseSection.offsetTop;
                    const sectionHeight = whyChooseSection.offsetHeight;
                    const sectionBottom = sectionTop + sectionHeight;
                    const viewportBottom = currentScroll + window.innerHeight;
                    isInWhyChoose = currentScroll >= sectionTop - 120 && viewportBottom <= sectionBottom + 50;
                }
                
                // Check if we're in "The People Behind" section
                if (peopleBehindSection) {
                    const sectionTop = peopleBehindSection.offsetTop;
                    const sectionHeight = peopleBehindSection.offsetHeight;
                    const sectionBottom = sectionTop + sectionHeight;
                    const viewportBottom = currentScroll + window.innerHeight;
                    isInPeopleBehind = currentScroll >= sectionTop - 120 && viewportBottom <= sectionBottom + 50;
                }
                
                // If we're in "Why Choose" or "The People Behind" sections, lock scrolling (only allow scroll snap)
                if (isInWhyChoose || isInPeopleBehind) {
                    e.preventDefault();
                    
                    let targetSection = null;
                    
                    if (e.deltaY > 0) {
                        // Scrolling down - find next section
                        sections.forEach((section) => {
                            const sectionTop = section.offsetTop;
                            if (sectionTop > currentScroll + 100 && !targetSection) {
                                targetSection = section;
                            }
                        });
                    } else {
                        // Scrolling up - find previous section
                        for (let i = sections.length - 1; i >= 0; i--) {
                            const section = sections[i];
                            const sectionTop = section.offsetTop;
                            if (sectionTop < currentScroll - 100) {
                                targetSection = section;
                                break;
                            }
                        }
                    }
                    
                    if (targetSection) {
                        targetSection.scrollIntoView({ 
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }
                    return;
                }
                
                // For other sections, allow normal scroll snap behavior
                let targetSection = null;
                
                if (e.deltaY > 0) {
                    sections.forEach((section) => {
                        const sectionTop = section.offsetTop;
                        if (sectionTop > currentScroll + 100 && !targetSection) {
                            targetSection = section;
                        }
                    });
                } else {
                    for (let i = sections.length - 1; i >= 0; i--) {
                        const section = sections[i];
                        const sectionTop = section.offsetTop;
                        if (sectionTop < currentScroll - 100) {
                            targetSection = section;
                            break;
                        }
                    }
                }
                
                if (targetSection) {
                    e.preventDefault();
                    targetSection.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }
        };

        window.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            window.removeEventListener('wheel', handleWheel);
        };
    }, []);

    // Measure heading width to align description (right edge of "m" to right edge of description)
    // Use base width without scale transform so description doesn't change on hover
    useEffect(() => {
        const updateHeadingWidth = () => {
            if (headingRef.current) {
                // Use requestAnimationFrame to ensure DOM is fully rendered
                requestAnimationFrame(() => {
                    // Get scrollWidth which gives the full content width
                    // This is the actual text content width without transforms
                    const contentWidth = headingRef.current.scrollWidth;
                    
                    // Also get offsetWidth as backup
                    const offsetWidth = headingRef.current.offsetWidth;
                    
                    // Use scrollWidth as it's more accurate for text content
                    // If scrollWidth is 0 or invalid, fall back to offsetWidth
                    const width = contentWidth > 0 ? contentWidth : offsetWidth;
                    
                    setHeadingWidth(width);
                });
            }
        };

        // Initial measurement with delay to ensure heading is rendered
        if (contentVisible) {
            const timer = setTimeout(updateHeadingWidth, 200);
            const timer2 = setTimeout(updateHeadingWidth, 500);
            updateHeadingWidth(); // Also try immediately
            
            // Only update on resize, NOT on scale changes
            window.addEventListener('resize', updateHeadingWidth);
            
            return () => {
                clearTimeout(timer);
                clearTimeout(timer2);
                window.removeEventListener('resize', updateHeadingWidth);
            };
        }
    }, [contentVisible]); // Removed headingScale from dependencies

    // Cursor interaction with particles - track mouse (disabled on phone only; tablet/iPad/desktop get it)
    useEffect(() => {
        if (isPhoneOnly) return;
        const handleMouseMove = (e) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
            
            // Calculate button scale based on cursor proximity
            if (buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                const buttonCenterX = rect.left + rect.width / 2;
                const buttonCenterY = rect.top + rect.height / 2;
                
                const distanceX = e.clientX - buttonCenterX;
                const distanceY = e.clientY - buttonCenterY;
                const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);
                
                // Maximum distance for effect (200px)
                const maxDistance = 200;
                
                if (distance < maxDistance) {
                    // Scale from 1.0 to 1.15 based on proximity
                    const proximity = 1 - (distance / maxDistance);
                    const scale = 1 + (proximity * 0.15); // Max scale of 1.15
                    setButtonScale(scale);
                } else {
                    setButtonScale(1);
                }
            }
            
            // Calculate heading scale based on cursor proximity (exclude description area)
            if (headingRef.current) {
                // Check if cursor is over description - if so, don't scale heading
                let isOverDescription = false;
                if (descriptionRef.current) {
                    const descRect = descriptionRef.current.getBoundingClientRect();
                    isOverDescription = (
                        e.clientX >= descRect.left &&
                        e.clientX <= descRect.right &&
                        e.clientY >= descRect.top &&
                        e.clientY <= descRect.bottom
                    );
                }
                
                // Only apply scale if cursor is NOT over description
                if (!isOverDescription) {
                    const rect = headingRef.current.getBoundingClientRect();
                    const headingCenterX = rect.left + rect.width / 2;
                    const headingCenterY = rect.top + rect.height / 2;
                    
                    const distanceX = e.clientX - headingCenterX;
                    const distanceY = e.clientY - headingCenterY;
                    const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);
                    
                    // Maximum distance for effect (300px for heading - larger area)
                    const maxDistance = 300;
                    
                    if (distance < maxDistance) {
                        // Scale from 1.0 to 1.1 based on proximity (subtle for heading)
                        const proximity = 1 - (distance / maxDistance);
                        const scale = 1 + (proximity * 0.1); // Max scale of 1.1
                        setHeadingScale(scale);
                    } else {
                        setHeadingScale(1);
                    }
                } else {
                    setHeadingScale(1);
                }
            }
        };

        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [isPhoneOnly]);

    // Automatic slideshow for question mark side
    useEffect(() => {
        // Only start slideshow when loading screen transition is complete
        if (contentVisible) {
            // Start the slideshow
            scheduleNextSlide(currentSlide);
        } else {
            // Clear any pending timeouts if content is not visible yet
            if (slideshowTimeoutRef.current) {
                clearTimeout(slideshowTimeoutRef.current);
            }
        }

        const intervalRef = slideshowIntervalRef.current;
        const timeoutRef = slideshowTimeoutRef.current;
        return () => {
            if (intervalRef) {
                clearInterval(intervalRef);
            }
            if (timeoutRef) {
                clearTimeout(timeoutRef);
            }
        };
    }, [currentSlide, scheduleNextSlide, contentVisible]);

    // Handle click - advance immediately but continue auto-advancing
    const handleSlideshowClick = () => {
        // Don't allow manual advancement until loading screen transition is complete
        if (!contentVisible) {
            return;
        }
        
        // Clear any pending timeout
        if (slideshowTimeoutRef.current) {
            clearTimeout(slideshowTimeoutRef.current);
        }
        
        // Advance to next slide immediately
        setCurrentSlide((prev) => {
            const nextSlide = (prev + 1) % slideshowImages.length;
            // Schedule next slide with appropriate delay
            scheduleNextSlide(nextSlide);
            return nextSlide;
        });
    };

    // Calculate particle interactions - Cluster 2 follows cursor (skipped on phone only; tablet/iPad/desktop get it)
    useEffect(() => {
        if (isPhoneOnly) return;
        if (mousePosition.x === 0 && mousePosition.y === 0) return;
        
        const styles = {};
        const cluster2Indices = [4, 5, 6, 7]; // Cluster 2 particles (center right) - indices 5-8 in CSS (0-indexed: 4-7)
        
        // Cluster 2 follows cursor - all particles in cluster move together as a group
        // Calculate cluster center position
        let clusterCenterX = 0;
        let clusterCenterY = 0;
        let validParticles = 0;
        
        cluster2Indices.forEach((index) => {
            const particle = document.querySelector(`.floating-particle-${index}`) || document.querySelector(`.home-particle-${index}`);
            if (particle) {
                const rect = particle.getBoundingClientRect();
                clusterCenterX += rect.left + rect.width / 2;
                clusterCenterY += rect.top + rect.height / 2;
                validParticles++;
            }
        });
        
        if (validParticles > 0) {
            clusterCenterX /= validParticles;
            clusterCenterY /= validParticles;
            
            // Calculate offset for cluster center to follow cursor
            const targetX = mousePosition.x - clusterCenterX;
            const targetY = mousePosition.y - clusterCenterY;
            const followSpeed = 0.12; // Smooth cluster following
            
            // Apply same offset to all particles in cluster
            cluster2Indices.forEach((index) => {
                const particleKey = `particle-${index}`;
                const particle = document.querySelector(`.floating-particle-${index}`) || document.querySelector(`.home-particle-${index}`);
                if (particle) {
                    const offsetX = targetX * followSpeed;
                    const offsetY = targetY * followSpeed;
                    
                    styles[particleKey] = {
                        transform: `translate(${offsetX}px, ${offsetY}px) scale(1.2)`,
                        opacity: 1,
                        filter: 'blur(6px)',
                        boxShadow: '0 0 40px rgba(255, 120, 40, 1), 0 0 80px rgba(255, 120, 40, 0.7)',
                    };
                }
            });
        }
        
        // Update other particles (not in following cluster) - check both landing and homepage particles
        for (let i = 0; i < 4; i++) {
            // Skip cluster 2 particles (already handled above)
            if (cluster2Indices.includes(i)) continue;
            
            const particleKey = `particle-${i}`;
            const particle = document.querySelector(`.floating-particle-${i}`) || document.querySelector(`.home-particle-${i}`);
            if (particle) {
                styles[particleKey] = {
                    transform: 'translate(0, 0) scale(1)',
                    opacity: 0.6,
                    filter: 'blur(8px)',
                    boxShadow: '0 0 30px rgba(255, 120, 40, 0.8), 0 0 60px rgba(255, 120, 40, 0.5)',
                };
            }
        }
        
        // Update scattered individual particles (indices 17-19)
        for (let i = 17; i < 20; i++) {
            const particleKey = `particle-${i}`;
            const particle = document.querySelector(`.floating-particle-${i}`) || document.querySelector(`.home-particle-${i}`);
            if (particle) {
                styles[particleKey] = {
                    transform: 'translate(0, 0) scale(1)',
                    opacity: 0.6,
                    filter: 'blur(8px)',
                    boxShadow: '0 0 30px rgba(255, 120, 40, 0.8), 0 0 60px rgba(255, 120, 40, 0.5)',
                };
            }
        }

        // Update floating particles type 2 - Cluster 3 (not following)
        for (let i = 0; i < 5; i++) {
            const particleKey = `particle-2-${i}`;
            const particle = document.querySelector(`.floating-particle-2-${i}`) || document.querySelector(`.home-particle-2-${i}`);
            if (particle) {
                styles[particleKey] = {
                    transform: 'translate(0, 0) scale(1)',
                    opacity: 0.5,
                    filter: 'blur(10px)',
                    boxShadow: '0 0 35px rgba(255, 120, 40, 0.7), 0 0 70px rgba(255, 120, 40, 0.4)',
                };
            }
        }
        
        // Update scattered type 2 particles
        for (let i = 4; i < 6; i++) {
            const particleKey = `particle-2-${i}`;
            const particle = document.querySelector(`.floating-particle-2-${i}`) || document.querySelector(`.home-particle-2-${i}`);
            if (particle) {
                styles[particleKey] = {
                    transform: 'translate(0, 0) scale(1)',
                    opacity: 0.5,
                    filter: 'blur(10px)',
                    boxShadow: '0 0 35px rgba(255, 120, 40, 0.7), 0 0 70px rgba(255, 120, 40, 0.4)',
                };
            }
        }

        // Update floating particles type 3 - Cluster 4 (not following)
        for (let i = 0; i < 4; i++) {
            const particleKey = `particle-3-${i}`;
            const particle = document.querySelector(`.floating-particle-3-${i}`) || document.querySelector(`.home-particle-3-${i}`);
            if (particle) {
                styles[particleKey] = {
                    transform: 'translate(0, 0) scale(1)',
                    opacity: 0.55,
                    filter: 'blur(12px)',
                    boxShadow: '0 0 40px rgba(255, 120, 40, 0.75), 0 0 80px rgba(255, 120, 40, 0.45)',
                };
            }
        }
        
        // Update scattered type 3 particles
        for (let i = 4; i < 6; i++) {
            const particleKey = `particle-3-${i}`;
            const particle = document.querySelector(`.floating-particle-3-${i}`) || document.querySelector(`.home-particle-3-${i}`);
            if (particle) {
                styles[particleKey] = {
                    transform: 'translate(0, 0) scale(1)',
                    opacity: 0.55,
                    filter: 'blur(12px)',
                    boxShadow: '0 0 40px rgba(255, 120, 40, 0.75), 0 0 80px rgba(255, 120, 40, 0.45)',
                };
            }
        }

        // Particle styles calculated but not used for now
    }, [mousePosition, isPhoneOnly]);

    const PAGE_BACKGROUND = 'linear-gradient(179.79deg, #060D1E 0.18%, #0A1633 66.06%, #0A1633 84.34%)';

  return (
        <>
            <LoadingScreen 
                onComplete={() => {}} 
                onTransitionComplete={() => setContentVisible(true)}
            />
            
            {/* Settings Floating Button - Desktop only (tablet/iPad use same as phone, no settings button) */}
            <div className="hidden min-[1368px]:flex fixed bottom-6 right-4 z-[99999] flex-col items-end gap-3" style={{ pointerEvents: 'auto' }}>
                <div 
                    className={`bg-[#0f1830]/70 border border-white/10 rounded-2xl p-3 shadow-2xl backdrop-blur-md w-52 space-y-3 transition-all duration-300 ease-in-out ${
                        settingsOpen 
                            ? 'opacity-100 translate-y-0 pointer-events-auto' 
                            : 'opacity-0 translate-y-2 pointer-events-none'
                    }`}
                >
                    <h4 className="text-xs font-semibold text-[#E68A40] tracking-wide">Settings</h4>
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-white/70 uppercase tracking-wider">Animation</span>
                        <button
                            onClick={() => setBackgroundEnabled((prev) => !prev)}
                            className={`w-14 h-8 rounded-full flex items-center px-1 transition-colors duration-300 ${backgroundEnabled ? 'bg-[#2c3853]' : 'bg-[#1a2133]'}`}
                        >
                            <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs transform transition-transform duration-300 ${backgroundEnabled ? 'translate-x-6 bg-[#E68A40]' : 'translate-x-0 bg-gray-500'}`}
                            >
                                <svg
                                    className="w-3 h-3"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </div>
                        </button>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-white/70 uppercase tracking-wider">Particle Effects</span>
                        <button
                            onClick={() => setCursorEnabled((prev) => !prev)}
                            className={`w-14 h-8 rounded-full flex items-center px-1 transition-colors duration-300 ${cursorEnabled ? 'bg-[#2c3853]' : 'bg-[#1a2133]'}`}
                        >
                            <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs transform transition-transform duration-300 ${cursorEnabled ? 'translate-x-6 bg-[#E68A40]' : 'translate-x-0 bg-gray-500'}`}
                            >
                                <svg
                                    className="w-3 h-3"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </div>
            </button>
          </div>
                </div>
                <button 
                    onClick={() => setSettingsOpen((prev) => !prev)}
                    className="w-12 h-12 rounded-full bg-[#0f162b]/90 border border-white/15 text-white flex items-center justify-center shadow-lg backdrop-blur-sm hover:bg-[#17203a] transition-all duration-300"
                >
                    <FaCog
                        className={`w-5 h-5 transition-transform duration-300 ${settingsOpen ? 'rotate-90 text-[#E68A40]' : 'text-white'}`}
                    />
                </button>
            </div>
            
            <div className="overflow-x-hidden scroll-smooth relative" ref={pageRef}>
            <div className="relative min-h-screen" style={{ background: PAGE_BACKGROUND }}>
                {/* Cursor Particle Animation - tablet/iPad: limit to background (first viewport) only */}
                <div 
                    className="fixed inset-0 pointer-events-none transition-opacity duration-700 ease-in-out z-10"
                    style={{
                        opacity: cursorEnabled ? 1 : 0,
                        ...(isTablet && { height: '100vh', overflow: 'hidden' }),
                    }}
                >
                    <CursorParticle />
                </div>
                
                {/* Nav Bar - Fixed at top */}
      <div className={`transition-opacity duration-1000 ease-out ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>
      <Navbar />
      </div>

                {/* === LANDING SECTION (Hero) with Enhanced Background === */}
            <section 
                id="landing-page"
                ref={landingSectionRef}
                className="snap-section relative min-h-screen flex items-center justify-center overflow-hidden"
                style={{ 
                    scrollSnapAlign: 'start', 
                    paddingTop: 'clamp(80px, 15vh, 120px)',
                    paddingBottom: 'clamp(40px, 8vh, 60px)',
                    background: 'transparent'
                }}
            >
                {/* Enhanced Interactive Background - static on phone only; tablet/iPad/desktop get animation */}
                <div 
                    className="absolute inset-0 transition-opacity duration-700 ease-in-out"
                    style={{
                        opacity: backgroundEnabled ? 1 : 0,
                        pointerEvents: backgroundEnabled ? 'auto' : 'none'
                    }}
                >
                    <EnhancedBackground currentSlide={currentSlide} />
                </div>
                
                {/* Subtle Overlay for better text readability - invisible barrier lines */}
                <div className="absolute inset-0 z-[1] pointer-events-none"></div>
                
                {/* Split Layout Container - Desktop only side-by-side; phone/tablet stacked (mobile view) */}
                <div className="relative z-10 w-full h-full flex flex-col min-[1368px]:flex-row">
                    {/* Left Side - Content Section */}
                    <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 md:px-10 min-[1368px]:px-16 xl:px-24 2xl:px-28 py-8 sm:py-12 min-[1368px]:py-0 relative">
                        {/* Content Container with balanced spacing */}
                        <div className="max-w-2xl">
                            {/* Main Heading - TheAdox.com - Updated format - Fade in after transition */}
                            <div className={`mb-3 sm:mb-4 transition-opacity duration-700 ease-out ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>
                                <h1 
                                    ref={headingRef}
                                    className="text-[2.5rem] xs:text-[3rem] sm:text-[3.5rem] md:text-[4.5rem] min-[1368px]:text-[5.5rem] xl:text-[6.25rem] 2xl:text-[6.75rem] font-heading font-bold leading-[0.9] tracking-tight drop-shadow-lg transition-transform duration-300 ease-out"
                                    style={{
                                        transform: `scale(${headingScale})`,
                                        transformOrigin: 'left center',
                                    }}
                                >
                                    <span className="text-[#E68A40]">The</span><span className="text-white">Adox.com</span>
          </h1>
                            </div>

                            {/* Subtitle - VIRTUAL INTELLIGENCE - Updated format - Fade in after transition */}
                            <div className={`mb-4 sm:mb-6 transition-opacity duration-700 ease-out ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>
                                <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl text-white font-normal tracking-[0.1em] sm:tracking-[0.12em] md:tracking-[0.15em] uppercase break-words">
                                    VIRTUAL INTELLIGENCE
                                </p>
                                <div className="w-12 sm:w-16 md:w-20 h-0.5 bg-gradient-to-r from-transparent via-[#E68A40] to-transparent rounded-full mt-2 sm:mt-3"></div>
        </div>

                            {/* Description - Enhanced typography with balanced spacing */}
                            <div className={`mb-6 sm:mb-8 transition-opacity duration-1000 ease-out ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>
                                <p 
                                    ref={descriptionRef}
                                    className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-white/85 leading-relaxed font-light text-justify"
                                    style={{
                                        width: headingWidth && window.innerWidth >= 1368 ? `${headingWidth}px` : 'auto',
                                        maxWidth: headingWidth && window.innerWidth >= 1368 ? `${headingWidth}px` : 'none',
                                        transform: 'none',
                                        transition: 'none',
                                        pointerEvents: 'auto'
                                    }}
                                >
                                    At theadox.com, we deliver intelligent online and virtual services supported by dependable, long-term client partnerships. In an environment that necessitates deft decision-making, we act as your strategic partner—integrating robust, data-driven insights through flexible, non-subscription solutions to optimise operations, enhance efficiency, and maximise productivity.
          </p>
                            </div>

                            {/* CTA Button - Premium Attractive Design with Cursor Proximity Effect - Aligned with 500+ row */}
                            <div className={`mt-4 sm:mt-8 md:mt-12 min-[1368px]:mt-20 xl:mt-32 2xl:mt-40 transition-all duration-1000 ease-out delay-300 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                                <button 
                                    ref={buttonRef}
                                    onClick={() => {
                                        const letsConnectElement = document.getElementById('lets-connect');
                                        if (letsConnectElement) {
                                            letsConnectElement.scrollIntoView({ 
                                                behavior: 'smooth',
                                                block: 'start'
                                            });
                                        }
                                    }}
                                    className="group relative w-full sm:w-auto px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-gradient-to-r from-[#E68A40] via-[#ffa64d] to-[#ffb366] text-white rounded-xl sm:rounded-2xl font-bold text-sm sm:text-base md:text-lg tracking-wider overflow-hidden transition-all duration-300 hover:-translate-y-0.5 active:scale-100"
                                    style={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        transform: `scale(${buttonScale})`,
                                        transition: 'transform 0.2s ease-out',
                                    }}
                                >
                                    {/* Button text with enhanced styling */}
                                    <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
                                        <span className="drop-shadow-lg">INQUIRE NOW</span>
                                        <svg 
                                            className="w-5 h-5 sm:w-6 sm:h-6 transform group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300 drop-shadow-lg" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                        </svg>
                                    </span>
            </button>
            </div>
          </div>
        </div>

                    {/* Right Side - Visual Element with Slideshow */}
                    <div className={`flex-1 flex items-center justify-center px-4 sm:px-6 md:px-8 min-[1368px]:px-12 xl:px-16 2xl:px-20 py-8 sm:py-12 min-[1368px]:py-0 relative transition-all duration-1000 ease-out delay-200 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                        {/* Slideshow Container */}
                        <div className="relative w-full flex flex-col items-center justify-center max-w-md min-[1368px]:max-w-none">
                            {/* Slideshow Items - Blank Image Box */}
                            <div 
                                data-placeholder-container="true"
                                className="relative mx-auto cursor-pointer z-10 transition-all duration-500 w-[200px] h-[200px] xs:w-[240px] xs:h-[240px] sm:w-[280px] sm:h-[280px] md:w-[340px] md:h-[340px] min-[1368px]:w-[400px] min-[1368px]:h-[400px] xl:w-[480px] xl:h-[480px] 2xl:w-[520px] 2xl:h-[520px]"
                                onClick={handleSlideshowClick}
                            >
                                {/* Render slides dynamically */}
                                {slideshowImages.map((image, index) => (
                                    <div 
                                        key={index}
                                        className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${
                                            currentSlide === index ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                                        }`}
                                    >
                                        {image === null ? (
                                            /* Animated Logo - Shows when image is null (slides 1 and 3) */
                                            <div 
                                                data-landing-logo
                                                className="relative w-full h-full flex items-center justify-center"
                                            >
                                                <img 
                                                    src={logoImage} 
                                                    alt="The Adox Logo" 
                                                    className="w-full h-full object-contain animate-logo-float animate-logo-pulse"
                                                    style={{
                                                        filter: 'drop-shadow(0 0 20px rgba(230, 138, 64, 0.6))',
                                                    }}
                                                />
                                                {/* Glow effect */}
                                                <div className="absolute inset-0 bg-[#E68A40]/20 rounded-full blur-3xl animate-logo-glow"></div>
                                            </div>
                                        ) : (
                                            /* Blank Image Box Container for company photos */
                                            <div className="relative w-full h-full bg-transparent border-transparent rounded-lg overflow-hidden flex items-center justify-center">
                                                {/* Company photo */}
                                                <img 
                                                    src={image} 
                                                    alt={`Slide ${index + 1}`}
                                                    className="w-full h-full object-contain object-center"
                                                    style={{
                                                        objectPosition: 'center center'
                                                    }}
                                                />
                </div>
                                        )}
              </div>
            ))}
          </div>

                            {/* Slideshow Indicators - Centered below image */}
                            <div className="flex justify-center items-center gap-2 sm:gap-3 mt-4 sm:mt-6 mb-2 sm:mb-4 w-full">
                                {slideshowImages.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            // Don't allow manual advancement until loading screen transition is complete
                                            if (!contentVisible) {
                                                return;
                                            }
                                            
                                            // Clear any pending timeout
                                            if (slideshowTimeoutRef.current) {
                                                clearTimeout(slideshowTimeoutRef.current);
                                            }
                                            
                                            // Go to selected slide
                                            setCurrentSlide(index);
                                            
                                            // Schedule next slide with appropriate delay
                                            scheduleNextSlide(index);
                                        }}
                                        className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                                            currentSlide === index 
                                                ? 'bg-[#E68A40] w-6 sm:w-8' 
                                                : 'bg-[#E68A40]/30 hover:bg-[#E68A40]/50 w-1.5 sm:w-2'
                                        }`}
                                    />
                ))}
              </div>
                        </div>
                    </div>
                </div>

                {/* Scroll Down indicator - desktop only; phones/tablets/iPads: hidden */}
                <div 
                    className={`absolute bottom-6 sm:bottom-8 md:bottom-12 left-0 right-0 flex justify-center text-white z-10 cursor-pointer group transition-opacity duration-1000 ease-out ${contentVisible && showScrollDownIndicator ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    onClick={() => {
                        const whatWeOfferElement = document.getElementById('what-we-offer');
                        if (whatWeOfferElement) {
                            whatWeOfferElement.scrollIntoView({ 
                                behavior: 'smooth',
                                block: 'start'
                            });
                        }
                    }}
                >
                    <div className="flex flex-col items-center gap-2 sm:gap-3">
                        <span className="text-xs sm:text-sm md:text-base font-medium tracking-wider opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                            Scroll Down
                        </span>
                        {/* Scroll down icon - mouse wheel (desktop only) */}
                        <div className="relative">
                            <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/60 rounded-full flex justify-center items-start pt-1.5 sm:pt-2 group-hover:border-white transition-colors duration-300">
                                <div className="w-1 h-2 sm:w-1.5 sm:h-3 bg-white/80 rounded-full animate-bounce group-hover:bg-white transition-colors duration-300"></div>
                            </div>
                            <div className="absolute inset-0 w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/30 rounded-full animate-ping opacity-20"></div>
                        </div>
                    </div>
                </div>
      </section>

                {/* === USER HOMEPAGE CONTENT STARTS HERE === */}
                <div className="overflow-x-hidden">
                {/* Enhanced Interactive Background - phone: few dots/lines; tablet: fewer; desktop: full */}
                    {backgroundEnabled && <EnhancedBackground />}
                {/* What We Offer Section - Each item is its own snap section */}
                <div id="what-we-offer" className="what-we-offer-section">
                    <WhatWeOffer />
          </div>

                {/* Why Choose TheAdox.com Section */}
                <section id="why-choose" className="snap-section min-h-screen flex items-center bg-transparent" style={{ scrollSnapAlign: 'start', paddingTop: 'clamp(80px, 15vh, 120px)' }}>
                    <div className="w-full max-w-none mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 bg-transparent" style={{ backgroundColor: 'transparent' }}>
                        <WhyChooseTheAdox />
        </div>
      </section>

                {/* The People Behind TheAdox.com Section - Original Layout */}
                <div className="snap-section people-behind-section" style={{ scrollSnapAlign: 'start', paddingTop: 'clamp(80px, 15vh, 120px)' }}>
                    <ThePeopleBehind />
                    </div>

                    {/* Let's Connect - scroll snap on phone/desktop only; NO scroll snap on tablet/iPad */}
                    <div
                        id="lets-connect"
                        className={`min-h-screen flex items-center ${isTablet ? '' : 'snap-section'}`}
                        style={{
                            scrollSnapAlign: isTablet ? undefined : 'start',
                            paddingTop: 'clamp(80px, 15vh, 120px)',
                        }}
                    >
                        <LetsConnect />
                    </div>

                    {/* Footer - NO scroll snap; normal flow so it always displays when you scroll (tablet/iPad) */}
                    <div id="footer">
                        <Footer />
                    </div>
                </div>
            </div>
            </div>
        </>
  );
};

export default UserLandingPage;